import { plain } from '../../lib/labels';
import { Avatar, Card, Col, Empty, Row, Segmented, Skeleton, Space, Tag, Typography } from 'antd';
import {
  AudioOutlined,
  BankOutlined,
  BellOutlined,
  BugOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FlagOutlined,
  GiftOutlined,
  GoldOutlined,
  PhoneOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WalletOutlined,
  WarningOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Area } from '@ant-design/plots';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../../api/client';
import { dashboardLinks, dayRange } from './dashboardLinks';
import { useAuth, usePermissions } from '../../auth/store';
import { useAttention } from '../../layout/AppLayout';
import { ListenerName, UserName, listenerLabel, userLabel } from '../../lib/listenerName';
import { RANGE_PRESETS, type StatsRangeParams } from '../listeners/listenerStats';
import { StatsRangeFilter, useUrlStatsRange } from '../listeners/ListenerStatsWidgets';

// ---------- Types ----------

interface AdminKpis {
  activeCalls: number;
  onlineUsers: number;
  revenueTodayPaise: string;
  revenueMonthPaise: string;
  pendingKyc: number;
  pendingPayouts: number;
  moderationBacklog: number;
  failedCallsToday: number;
  failedCallsYesterday: number;
  incidentsOpen: number;
  incidentsFatal24h: number;
  onboarding: OnboardingFunnel;
  generatedAt: string;
}

interface OnboardingFunnel {
  totalUsers: number;
  completedUsers: number;
  completionPct: number;
  funnel: { step: number; label: string; incomplete: number }[];
}

interface RevenueBucket {
  bucket: string;
  platformFeePaise: string;
  rechargePaise: string;
  refundPaise: string;
  listenerEarningsPaise?: string;
}

interface Incident {
  id: string;
  severity: 'fatal' | 'error' | 'warning' | 'info';
  category: string;
  message: string;
  platform: string;
  status?: 'open' | 'acknowledged' | 'resolved' | 'ignored';
  createdAt: string;
}

interface HealthDeps {
  status: 'ok' | 'degraded';
  dependencies?: {
    postgres?: { reachable: boolean };
    redis?: { reachable: boolean };
  };
}

interface ActiveCallRow {
  id: string;
  userId?: string;
  listenerId?: string;
  userHandle?: string;
  /** The caller's own Display Name — absent on older APIs. */
  userDisplayName?: string | null;
  listenerHandle?: string;
  listenerDisplayName?: string;
  mode: 'audio' | 'video';
  status?: string;
  outcome?: string | null;
  durationSeconds?: number | null;
  startedAt: string | null;
}

interface LiveStreamRow {
  id: string;
  title: string;
  streamer: { id: string; handle: string; displayName?: string | null; userDisplayName?: string | null };
  viewerCount: number;
  startedAt: string;
}

/** `GET /admin/console/insights?days` — growth per IST day + who the users are. */
export interface ConsoleInsights {
  days: number;
  growth: Array<{ day: string; newUsers: number; activeUsers: number; totalUsers: number }>;
  languages: { top: Array<{ code: string; users: number }>; other: number; total: number };
  devices: Array<{ platform: string; users: number }>;
  generatedAt: string;
}

// ---------- Helpers ----------

function rupees(paise: string | number): string {
  const n = typeof paise === 'string' ? BigInt(paise) : BigInt(paise);
  const v = Number(n) / 100;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function mmss(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function callDuration(startedAt: string | null): string {
  if (!startedAt) return '—';
  return mmss(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
}

const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** Percent change from `prev` to `now`; null when there is nothing to compare with. */
export function change(now: number, prev: number): number | null {
  if (prev <= 0) return null;
  return Math.round(((now - prev) / prev) * 100);
}

let languageNames: Intl.DisplayNames | null = null;
function languageName(code: string): string {
  try {
    languageNames ??= new Intl.DisplayNames(['en'], { type: 'language' });
    return languageNames.of(code) ?? code;
  } catch {
    return code;
  }
}

// ---------- Visual primitives ----------

const CARD_BG = 'rgba(255,255,255,0.03)';
const CARD_BORDER = 'rgba(255,255,255,0.07)';
const ACCENT_PINK = '#FB3C7F';
const ACCENT_GOLD = '#FFD06A';
const ACCENT_TEAL = '#10B981';
const ACCENT_BLUE = '#5B7CFA';
const ACCENT_RED = '#EF4444';
const ACCENT_AMBER = '#F59E0B';
const ACCENT_PURPLE = '#8B5CF6';
const MUTED = '#6B7280';

/** The hero row's gradients (mockup: pink, blue, orange, slate). */
const GRADIENTS = {
  pink: 'linear-gradient(135deg, #C026D3 0%, #FB3C7F 100%)',
  blue: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 100%)',
  orange: 'linear-gradient(135deg, #F97316 0%, #F43F5E 100%)',
  slate: 'linear-gradient(135deg, #334155 0%, #1E293B 100%)',
} as const;

function SectionTitle({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <Space align="baseline" style={{ marginTop: 4 }}>
      <Typography.Title level={5} style={{ color: 'white', margin: 0, fontWeight: 700 }}>
        {children}
      </Typography.Title>
      {hint && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          · {hint}
        </Typography.Text>
      )}
    </Space>
  );
}

// ---------- Console data (shared with Analytics + Reports) ----------

/** `GET /admin/console/realtime` — never affected by the date filter. */
export interface ConsoleRealtime {
  onlineListeners: number;
  busyListeners: number;
  liveListeners: number;
  offlineListeners: number;
  approvedListeners: number;
  activeCalls: number;
  ringingCalls: number;
  liveStreams: number;
  generatedAt: string;
}

/** `GET /admin/console/metrics?from&to` — figures for the selected period. */
export interface ConsoleMetrics {
  range: { from: string; to: string };
  users: {
    newUsers: number;
    totalUsers: number;
    usersWhoRecharged: number;
    registrationCompleted: number;
    registrationCompletionPct: number | null;
  };
  calls: {
    total: number;
    completed: number;
    missed: number;
    declined: number;
    cancelled: number;
    failed: number;
    audioSeconds: number;
    videoSeconds: number;
    totalSeconds: number;
    coinsCharged: number;
  };
  money: {
    rechargePaise: string;
    recharges: number;
    coinsPurchased: number;
    bonusCoins: number;
    listenerEarningsPaise: string;
    platformMarginPaise: string;
    avgRevenuePerCallPaise: string | null;
  };
  listeners: { approved: number; newApplications: number; rejected: number };
}

/** Period metrics for a date range — one query shared by every page that shows them. */
export function useConsoleMetrics(range: StatsRangeParams | null) {
  return useQuery({
    queryKey: ['admin', 'console', 'metrics', range],
    queryFn: async () => (await api.get<ConsoleMetrics>('/admin/console/metrics', { params: range })).data,
    enabled: range !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/** Whole minutes, e.g. "1,240 min". */
export function minutes(seconds: number): string {
  return `${Math.round(seconds / 60).toLocaleString('en-IN')} min`;
}

/** The period of the same length just before `range` — what "vs yesterday" compares with. */
export function previousRange(range: StatsRangeParams): StatsRangeParams {
  const from = new Date(range.from).getTime();
  const len = new Date(range.to).getTime() - from;
  return { ...range, from: new Date(from - len).toISOString(), to: new Date(from).toISOString() };
}

// ---------- Small building blocks ----------

/** Up/down change: "↑ +18% vs yesterday". */
function Delta({ value, label }: { value: number; label: string }) {
  const up = value >= 0;
  return (
    <span style={{ fontWeight: 600, color: up ? '#34D399' : '#F87171' }}>
      {up ? '↑ +' : '↓ −'}
      {Math.abs(value)}% {label}
    </span>
  );
}

/** Round icon chip in the corner of a stat card. */
function IconChip({ icon, color, solid }: { icon: ReactNode; color: string; solid?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0,
        color: solid ? 'white' : color,
        background: solid ? 'rgba(255,255,255,0.18)' : `${color}26`,
        border: solid ? '1px solid rgba(255,255,255,0.25)' : `1px solid ${color}40`,
      }}
    >
      {icon}
    </span>
  );
}

/** One number that opens the page listing what it counted. `gradient` = hero style. */
function StatCard({
  label,
  value,
  hint,
  to,
  accent = ACCENT_BLUE,
  icon,
  gradient,
  dot,
  loading,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  to?: string;
  accent?: string;
  icon?: ReactNode;
  gradient?: string;
  /** Small status dot before the label (hero cards). */
  dot?: string;
  loading?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Card
      hoverable={!!to}
      role={to ? 'link' : undefined}
      aria-label={to ? `${label} — open` : undefined}
      onClick={to ? () => navigate(to) : undefined}
      style={{
        height: '100%',
        cursor: to ? 'pointer' : 'default',
        background: gradient ?? CARD_BG,
        border: gradient ? 'none' : `1px solid ${CARD_BORDER}`,
        borderRadius: 14,
      }}
      styles={{ body: { padding: 16 } }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: gradient ? 'rgba(255,255,255,0.9)' : '#9CA3AF', display: 'flex', alignItems: 'center', gap: 6 }}>
            {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block' }} />}
            {label}
          </div>
          <div style={{ color: 'white', fontSize: 26, fontWeight: 800, lineHeight: 1.25, marginTop: 4 }}>
            {loading ? <Skeleton.Button active size="small" /> : value}
          </div>
          {hint && (
            <div style={{ fontSize: 11, marginTop: 2, color: gradient ? 'rgba(255,255,255,0.8)' : '#9CA3AF' }}>{hint}</div>
          )}
        </div>
        {icon && <IconChip icon={icon} color={accent} solid={!!gradient} />}
      </div>
    </Card>
  );
}

/** A queue that needs someone: red when work is waiting, quiet when clear. */
function AttentionCard({
  label,
  count,
  to,
  icon,
}: {
  label: string;
  count: number;
  to: string;
  icon: ReactNode;
}) {
  const navigate = useNavigate();
  const waiting = count > 0;
  return (
    <Card
      hoverable
      role="link"
      aria-label={`${label}: ${count}`}
      onClick={() => navigate(to)}
      style={{
        height: '100%',
        cursor: 'pointer',
        borderRadius: 12,
        background: waiting ? 'rgba(239,68,68,0.08)' : CARD_BG,
        border: `1px solid ${waiting ? 'rgba(239,68,68,0.45)' : CARD_BORDER}`,
      }}
      styles={{ body: { padding: '10px 14px' } }}
    >
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: waiting ? '#FCA5A5' : '#6EE7B7', fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>
            {count.toLocaleString('en-IN')}
          </div>
          <Typography.Text style={{ color: 'white', fontSize: 12 }}>{label}</Typography.Text>
        </div>
        <span style={{ fontSize: 16, color: waiting ? '#FCA5A5' : MUTED }}>{icon}</span>
      </Space>
    </Card>
  );
}

interface BarRow {
  label: string;
  value: number;
  color: string;
  to?: string;
  /** Small grey figure before the value, e.g. a share "68%". */
  extra?: string;
  icon?: ReactNode;
}

/** Labelled horizontal bars (share of the largest) — minutes, presence, languages. */
function BarList({
  rows,
  format = (n: number) => n.toLocaleString('en-IN'),
  labelWidth = 92,
}: {
  rows: BarRow[];
  format?: (n: number) => string;
  labelWidth?: number;
}) {
  const navigate = useNavigate();
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {rows.map((r) => (
        <div
          key={r.label}
          role={r.to ? 'link' : undefined}
          onClick={r.to ? () => navigate(r.to!) : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: r.to ? 'pointer' : undefined }}
        >
          <span style={{ width: labelWidth, fontSize: 12, flexShrink: 0, color: '#D1D5DB', display: 'flex', alignItems: 'center', gap: 6 }}>
            {r.icon && <span style={{ color: r.color }}>{r.icon}</span>}
            {r.label}
          </span>
          <div style={{ flex: 1, height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
            <div
              style={{
                width: `${(r.value / max) * 100}%`,
                minWidth: r.value > 0 ? 3 : 0,
                height: '100%',
                borderRadius: 999,
                background: r.color,
              }}
            />
          </div>
          {r.extra !== undefined && <span style={{ width: 40, textAlign: 'right', fontSize: 11, color: '#9CA3AF' }}>{r.extra}</span>}
          <Typography.Text style={{ width: 70, textAlign: 'right', color: 'white', fontWeight: 600, fontSize: 12 }}>
            {format(r.value)}
          </Typography.Text>
        </div>
      ))}
    </Space>
  );
}

/** Ring chart with a figure in the middle and a legend beside it. Plain SVG. */
function Donut({
  center,
  caption,
  slices,
}: {
  center: ReactNode;
  caption: string;
  slices: Array<{ label: string; value: number; color: string; to?: string }>;
}) {
  const navigate = useNavigate();
  const total = slices.reduce((n, s) => n + s.value, 0);
  const R = 52;
  const C = 2 * Math.PI * R;
  const arcs = slices.reduce<Array<{ label: string; color: string; len: number; offset: number }>>((acc, s) => {
    const prev = acc.at(-1);
    acc.push({ label: s.label, color: s.color, len: total > 0 ? (s.value / total) * C : 0, offset: prev ? prev.offset + prev.len : 0 });
    return acc;
  }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
        <svg viewBox="0 0 140 140" width={140} height={140} role="img" aria-label={caption}>
          <circle cx={70} cy={70} r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={14} />
          {arcs.map((a) => (
            <circle
              key={a.label}
              cx={70}
              cy={70}
              r={R}
              fill="none"
              stroke={a.color}
              strokeWidth={14}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={-a.offset}
              transform="rotate(-90 70 70)"
            />
          ))}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: 'white', fontSize: 22, fontWeight: 800 }}>{center}</div>
          <div style={{ color: '#9CA3AF', fontSize: 11 }}>{caption}</div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        {slices.map((s) => (
          <div
            key={s.label}
            role={s.to ? 'link' : undefined}
            onClick={s.to ? () => navigate(s.to!) : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 13, cursor: s.to ? 'pointer' : undefined }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            <span style={{ color: '#D1D5DB', flex: 1 }}>{s.label}</span>
            <span style={{ color: 'white', fontWeight: 600, width: 56, textAlign: 'right' }}>{s.value.toLocaleString('en-IN')}</span>
            <span style={{ color: '#9CA3AF', width: 40, textAlign: 'right', fontSize: 12 }}>{pct(s.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const FUNNEL_COLORS = ['#A78BFA', '#C084FC', '#E879F9', '#F472B6', '#FB7185', '#FB923C', '#FBBF24', '#60A5FA'];

/** Users who REACHED each onboarding step: all users minus those who stopped at an earlier step. */
export function reachedPerStep(onboarding: OnboardingFunnel) {
  const steps = [...onboarding.funnel].sort((a, b) => a.step - b.step);
  return steps.reduce<Array<OnboardingFunnel['funnel'][number] & { reached: number }>>((acc, f) => {
    const prev = acc.at(-1);
    acc.push({ ...f, reached: prev ? prev.reached - prev.incomplete : onboarding.totalUsers });
    return acc;
  }, []);
}

/**
 * Registration funnel: how many users reached each step and their share of all
 * users, plus the completion rate. A step opens the users who stopped there.
 */
function RegistrationFunnel({ onboarding, onOpenStep }: { onboarding: OnboardingFunnel; onOpenStep: (step: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ width: 170, flexShrink: 0 }}>
        <div
          aria-hidden
          style={{
            height: 130,
            clipPath: 'polygon(0 0, 100% 0, 62% 100%, 38% 100%)',
            background: 'linear-gradient(180deg, #8B5CF6 0%, #EC4899 55%, #F59E0B 100%)',
          }}
        />
        <div style={{ color: 'white', fontSize: 28, fontWeight: 800, marginTop: 14 }}>{onboarding.completionPct}%</div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Completion rate · {onboarding.completedUsers.toLocaleString('en-IN')} of {onboarding.totalUsers.toLocaleString('en-IN')}
        </Typography.Text>
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        {reachedPerStep(onboarding).map((f, i) => (
          <div
            key={f.step}
            role="link"
            tabIndex={0}
            aria-label={`Users who stopped at ${f.label}`}
            onClick={() => onOpenStep(f.step)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onOpenStep(f.step);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', fontSize: 13, cursor: 'pointer' }}
          >
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] ?? MUTED, flexShrink: 0 }} />
            <span style={{ color: '#D1D5DB', flex: 1 }}>
              {f.step + 1}. {f.label}
            </span>
            <span style={{ color: 'white', fontWeight: 600, width: 60, textAlign: 'right' }}>{f.reached.toLocaleString('en-IN')}</span>
            <span style={{ color: '#9CA3AF', width: 44, textAlign: 'right', fontSize: 12 }}>{pct(f.reached, onboarding.totalUsers)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const chartCard: CSSProperties = { background: CARD_BG, border: `1px solid ${CARD_BORDER}`, height: '100%', borderRadius: 14 };
const cardBody = { body: { padding: 18 } };

const SEVERITY_COLOR: Record<Incident['severity'], string> = { fatal: 'magenta', error: 'error', warning: 'warning', info: 'default' };
const STATUS_COLOR: Record<NonNullable<Incident['status']>, string> = {
  open: 'red',
  acknowledged: 'orange',
  resolved: 'green',
  ignored: 'default',
};
const PLATFORM_COLOR: Record<string, string> = { android: ACCENT_TEAL, ios: ACCENT_BLUE, web: ACCENT_PURPLE };
const LANGUAGE_COLORS = [ACCENT_TEAL, ACCENT_BLUE, ACCENT_AMBER, ACCENT_PINK];

/** Seven equal columns: the 14 "More figures" cards fill two full rows on wide screens. */
const SEVENTH = { flex: '0 0 14.2857%' } as const;

type GrowthTab = 'newUsers' | 'activeUsers' | 'totalUsers';
type RevenueTab = 'received' | 'earnings';
type SessionsTab = 'calls' | 'streams' | 'recent';

// ---------- Page ----------

/**
 * The admin's morning check, top to bottom:
 *   1. Welcome + NEEDS ATTENTION — queues with work waiting (always live).
 *   2. Hero row — who is online / on a call / live (always live).
 *   3. Users, calls and money for the selected PERIOD, with the change from the
 *      period before it ("vs yesterday" when the filter is Today).
 *   4. Trends, funnel, outcomes, problems, live sessions, who the users are.
 * Every figure opens the page that lists what it counted.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const profile = useAuth((s) => s.profile);
  const { preset, setPreset, setCustom, range } = useUrlStatsRange('today');
  const periodLabel = RANGE_PRESETS.find((p) => p.value === preset)?.label ?? 'Selected period';
  const vsLabel = preset === 'today' ? 'vs yesterday' : 'vs previous period';
  const days = range ? dayRange(range) : null;
  const [growthTab, setGrowthTab] = useState<GrowthTab>('newUsers');
  const [revenueTab, setRevenueTab] = useState<RevenueTab>('received');
  const [sessionsTab, setSessionsTab] = useState<SessionsTab>('calls');

  // ---- Real-time (ignores the date filter) ----
  const attention = useAttention();
  const realtime = useQuery({
    queryKey: ['admin', 'console', 'realtime'],
    queryFn: async () => (await api.get<ConsoleRealtime>('/admin/console/realtime')).data,
    refetchInterval: 10_000,
  });
  const activeCalls = useQuery({
    queryKey: ['admin', 'dash', 'active-calls'],
    queryFn: async () =>
      (await api.get<{ items: ActiveCallRow[] }>('/admin/calls?status=connected&limit=6')).data,
    refetchInterval: 5_000,
  });
  const liveStreams = useQuery({
    queryKey: ['admin', 'dash', 'live-streams'],
    queryFn: async () =>
      (await api.get<{ items: LiveStreamRow[] }>('/admin/live/streams', { params: { status: 'live' } })).data,
    enabled: sessionsTab === 'streams' && can('live.view'),
    refetchInterval: 10_000,
  });
  const recentCalls = useQuery({
    queryKey: ['admin', 'dash', 'recent-calls'],
    queryFn: async () => (await api.get<{ items: ActiveCallRow[] }>('/admin/calls?limit=6')).data,
    enabled: sessionsTab === 'recent',
    refetchInterval: 15_000,
  });
  const recentIncidents = useQuery({
    queryKey: ['admin', 'dash', 'recent-incidents'],
    queryFn: async () => (await api.get<{ items: Incident[] }>('/admin/incidents?limit=6')).data,
    enabled: can('incidents.view'),
    refetchInterval: 15_000,
  });
  const health = useQuery({
    queryKey: ['admin', 'dash', 'health'],
    queryFn: async () => (await api.get<HealthDeps>('/health')).data,
    refetchInterval: 10_000,
  });
  // The registration funnel is a standing snapshot (where incomplete users sit now).
  const kpis = useQuery({
    queryKey: ['admin', 'kpis'],
    queryFn: async () => (await api.get<AdminKpis>('/admin/dashboard/kpis')).data,
    refetchInterval: 30_000,
  });

  // ---- Historical (the selected period, and the one before it) ----
  const metrics = useConsoleMetrics(range);
  const previous = useConsoleMetrics(range ? previousRange(range) : null);
  // Trend charts reach back to the start of the period, never fewer than 7 days.
  const trendDays = range
    ? Math.min(90, Math.max(7, Math.ceil((Date.now() - new Date(range.from).getTime()) / 86_400_000) + 1))
    : 7;
  const insights = useQuery({
    queryKey: ['admin', 'console', 'insights', trendDays],
    queryFn: async () =>
      (await api.get<ConsoleInsights>('/admin/console/insights', { params: { days: trendDays } })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const revenueDaily = useQuery({
    queryKey: ['admin', 'analytics-mini', 'revenue', trendDays],
    queryFn: async () =>
      (await api.get<RevenueBucket[]>('/admin/analytics/revenue-daily', { params: { days: trendDays } })).data,
    enabled: can('analytics.revenue.view'),
    refetchInterval: 60_000,
  });
  const revenueTrend = (revenueDaily.data ?? []).map((b) => ({
    day: new Date(b.bucket).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    received: Number(b.rechargePaise),
    earnings: Number(b.listenerEarningsPaise ?? 0),
  }));
  const revenueTotal = revenueTrend.reduce((n, r) => n + r[revenueTab], 0);
  const growthTrend = (insights.data?.growth ?? []).map((g) => ({
    day: new Date(`${g.day}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    value: g[growthTab],
  }));

  const m = metrics.data;
  const p = previous.data;
  const now = realtime.data;
  const a = attention.data;
  const systemOk =
    health.data?.status === 'ok' &&
    !!health.data.dependencies?.postgres?.reachable &&
    !!health.data.dependencies?.redis?.reachable;

  /** Only the queues this admin can open (null = no permission). */
  const queues: Array<{ label: string; count: number | null | undefined; to: string; icon: ReactNode }> = [
    { label: 'Pending listener applications', count: a?.pendingListeners, to: dashboardLinks.pendingListeners, icon: <TeamOutlined /> },
    { label: 'ID documents to review', count: a?.kycPending, to: dashboardLinks.kyc, icon: <FileDoneOutlined /> },
    { label: 'Voice checks to review', count: a?.voicePending, to: dashboardLinks.voiceVerification, icon: <AudioOutlined /> },
    { label: 'Payouts awaiting approval', count: a?.payoutsPending, to: dashboardLinks.payouts, icon: <BankOutlined /> },
    { label: 'Support messages waiting', count: a?.supportOpen, to: dashboardLinks.support, icon: <CustomerServiceOutlined /> },
    { label: 'Safety reports to review', count: a?.moderationPending, to: dashboardLinks.moderation, icon: <FlagOutlined /> },
    { label: 'Paid, coins not added', count: a?.paidNotCredited, to: dashboardLinks.paidNotCredited, icon: <WarningOutlined /> },
    { label: 'App problems reported', count: a?.incidentsOpen, to: dashboardLinks.openIncidents, icon: <BugOutlined /> },
    { label: 'Call delivery alerts', count: a?.deliveryAlerts, to: dashboardLinks.callDelivery, icon: <WifiOutlined /> },
  ];
  const visibleQueues = queues.filter((q): q is typeof q & { count: number } => typeof q.count === 'number');
  const waitingTotal = visibleQueues.reduce((n, q) => n + q.count, 0);

  const newUsersChange = m && p ? change(m.users.newUsers, p.users.newUsers) : null;
  const receivedChange = m && p ? change(Number(m.money.rechargePaise), Number(p.money.rechargePaise)) : null;
  const avgCallSeconds = m && m.calls.completed > 0 ? Math.round(m.calls.totalSeconds / m.calls.completed) : null;
  const deviceTotal = (insights.data?.devices ?? []).reduce((n, d) => n + d.users, 0);
  const languages = insights.data?.languages;

  const callRow = (c: ActiveCallRow, right: ReactNode) => {
    const listener = { displayName: c.listenerDisplayName, handle: c.listenerHandle, id: c.listenerId };
    return (
      <div
        key={c.id}
        onClick={() => navigate(dashboardLinks.liveCall(c.id))}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 0',
          borderBottom: `1px solid ${CARD_BORDER}`,
          fontSize: 13,
          cursor: 'pointer',
          flexWrap: 'wrap',
        }}
      >
        <Avatar size={26} style={{ background: ACCENT_PINK + '40', color: ACCENT_PINK, fontSize: 11, fontWeight: 700 }}>
          {userLabel({ displayName: c.userDisplayName, handle: c.userHandle }).replace(/^@/, '').replace(/^—$/, '?').slice(0, 2).toUpperCase()}
        </Avatar>
        <UserName handle={c.userHandle} displayName={c.userDisplayName} userId={c.userId} secondary="inline" />
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>↔</Typography.Text>
        <Avatar size={26} style={{ background: ACCENT_TEAL + '40', color: ACCENT_TEAL, fontSize: 11, fontWeight: 700 }}>
          {listenerLabel(listener).replace(/^@/, '').slice(0, 2).toUpperCase()}
        </Avatar>
        <ListenerName listener={listener} listenerId={c.listenerId} secondary="inline" />
        <Tag style={{ marginLeft: 4, fontSize: 10 }}>{plain(c.mode)}</Tag>
        <span style={{ marginLeft: 'auto' }}>{right}</span>
      </div>
    );
  };

  return (
    <Space direction="vertical" size={18} style={{ width: '100%' }} className="dash-page">
      <style>{`
        .dash-page .ant-card.ant-card-hoverable:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.45);
        }
        .dash-page .ant-empty-description { color: #6B7280; }
        .dash-page .ant-card-head { border-bottom: 1px solid rgba(255,255,255,0.06); min-height: 48px; padding: 0 18px; }
        .dash-page .ant-card-head-title { padding: 12px 0; }
        .dash-page .dash-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .dash-page .dash-table th { color: #9CA3AF; font-weight: 500; text-align: left; padding: 6px 8px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .dash-page .dash-table td { color: #E5E7EB; padding: 9px 8px; border-bottom: 1px solid rgba(255,255,255,0.04); }
        .dash-page .dash-table tr.dash-row { cursor: pointer; }
        .dash-page .dash-table tr.dash-row:hover td { background: rgba(255,255,255,0.03); }
      `}</style>

      {/* Welcome banner + the ONE date filter for every historical figure below. */}
      <div
        style={{
          borderRadius: 16,
          padding: '22px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          background:
            'radial-gradient(circle at 85% 30%, rgba(236,72,153,0.55), transparent 45%), linear-gradient(110deg, #1E1B4B 0%, #312E81 45%, #7C3AED 75%, #DB2777 100%)',
        }}
      >
        <div>
          <Typography.Title level={2} style={{ color: 'white', margin: 0, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Welcome back{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}! 👋
          </Typography.Title>
          <Typography.Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
            Here&apos;s what&apos;s happening with your app. Live figures update on their own; everything else follows the date filter.
          </Typography.Text>
        </div>
        <Space wrap>
          <span
            aria-label="System health"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              color: 'white',
              background: !health.data ? 'rgba(255,255,255,0.12)' : systemOk ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.35)',
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: !health.data ? '#9CA3AF' : systemOk ? '#34D399' : '#F87171' }} />
            {!health.data ? 'Checking…' : systemOk ? 'Normal' : 'Degraded'}
          </span>
          <StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} />
        </Space>
      </div>

      {/* ===== Needs attention ===== */}
      {(attention.isLoading || visibleQueues.length > 0) && (
        <>
          <SectionTitle hint={waitingTotal > 0 ? `${waitingTotal.toLocaleString('en-IN')} items waiting` : 'nothing waiting'}>
            Needs attention
          </SectionTitle>
          <Row gutter={[12, 12]}>
            {attention.isLoading && <Col span={24}><Skeleton active paragraph={{ rows: 1 }} /></Col>}
            {visibleQueues.map((q) => (
              <Col xs={12} md={8} xl={6} xxl={3} key={q.label}>
                <AttentionCard label={q.label} count={q.count} to={q.to} icon={q.icon} />
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* ===== Hero row: right now ===== */}
      <Row gutter={[14, 14]}>
        <Col xs={12} xl={6}>
          <StatCard label="Online listeners" value={now?.onlineListeners ?? '—'} to={dashboardLinks.onlineListeners} gradient={GRADIENTS.pink} dot="#34D399" icon={<CustomerServiceOutlined />} loading={realtime.isLoading} hint="can take a call now" />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Busy listeners" value={now?.busyListeners ?? '—'} to={dashboardLinks.busyListeners} gradient={GRADIENTS.blue} dot="#FDE047" icon={<PhoneOutlined />} loading={realtime.isLoading} hint="on a call now" />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Live listeners" value={now?.liveListeners ?? '—'} to={dashboardLinks.liveListeners} gradient={GRADIENTS.orange} dot="#FDE047" icon={<WifiOutlined />} loading={realtime.isLoading} hint="hosting a stream" />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Offline listeners" value={now?.offlineListeners ?? '—'} to={dashboardLinks.allListeners} gradient={GRADIENTS.slate} dot="#9CA3AF" icon={<TeamOutlined />} loading={realtime.isLoading} hint={now ? `of ${now.approvedListeners} approved` : undefined} />
        </Col>
      </Row>

      {metrics.error && (
        <Card>
          <Typography.Text type="danger">Could not load the figures for this period</Typography.Text>
        </Card>
      )}
      {range === null && (
        <Card>
          <Typography.Text type="secondary">Pick both dates of the custom range to see the period figures.</Typography.Text>
        </Card>
      )}

      {/* ===== Users + calls ===== */}
      <Row gutter={[14, 14]}>
        <Col xs={12} xl={6}>
          <StatCard
            label="New users"
            value={m?.users.newUsers.toLocaleString('en-IN') ?? '—'}
            to={dashboardLinks.users}
            accent={ACCENT_PURPLE}
            icon={<UserAddOutlined />}
            loading={metrics.isLoading}
            hint={newUsersChange === null ? `joined · ${periodLabel}` : <Delta value={newUsersChange} label={vsLabel} />}
          />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Total users" value={m?.users.totalUsers.toLocaleString('en-IN') ?? '—'} to={dashboardLinks.users} accent={ACCENT_BLUE} icon={<UserOutlined />} loading={metrics.isLoading} hint="all time, not deleted" />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Active calls" value={now?.activeCalls ?? '—'} to={dashboardLinks.activeCalls} accent={ACCENT_TEAL} icon={<PhoneOutlined />} loading={realtime.isLoading} hint="connected now" />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Calls ringing" value={now?.ringingCalls ?? '—'} to={dashboardLinks.ringingCalls} accent={ACCENT_BLUE} icon={<BellOutlined />} loading={realtime.isLoading} hint="not yet connected" />
        </Col>
      </Row>

      {/* ===== Money (by when the payment was received) ===== */}
      <Row gutter={[14, 14]}>
        <Col xs={12} xl={6}>
          <StatCard
            label="Money received"
            value={m ? rupees(m.money.rechargePaise) : '—'}
            to={days ? dashboardLinks.rechargesInRange(days) : undefined}
            accent={ACCENT_GOLD}
            icon={<GoldOutlined />}
            loading={metrics.isLoading}
            hint={
              receivedChange !== null ? (
                <Delta value={receivedChange} label={vsLabel} />
              ) : m ? (
                `${m.money.recharges} coin purchases paid`
              ) : undefined
            }
          />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Coins purchased" value={m?.money.coinsPurchased.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.rechargesInRange(days) : undefined} accent={ACCENT_AMBER} icon={<DollarOutlined />} loading={metrics.isLoading} hint={m ? <span style={{ color: '#34D399' }}>+ {m.money.bonusCoins.toLocaleString('en-IN')} bonus coins</span> : undefined} />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Listener earnings" value={m ? rupees(m.money.listenerEarningsPaise) : '—'} to={dashboardLinks.coinsPerListener} accent={ACCENT_PINK} icon={<GiftOutlined />} loading={metrics.isLoading} hint="calls, gifts, chat and credits" />
        </Col>
        <Col xs={12} xl={6}>
          <StatCard label="Left after paying listeners" value={m ? rupees(m.money.platformMarginPaise) : '—'} to={dashboardLinks.revenueChart} accent={ACCENT_PINK} icon={<WalletOutlined />} loading={metrics.isLoading} hint="estimate: money received minus listener earnings" />
        </Col>
      </Row>

      {/* ===== Growth + revenue trends ===== */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={can('analytics.revenue.view') ? 12 : 24}>
          <Card
            title="User growth"
            extra={
              <Segmented<GrowthTab>
                size="small"
                value={growthTab}
                onChange={setGrowthTab}
                options={[
                  { label: 'New users', value: 'newUsers' },
                  { label: 'Active users', value: 'activeUsers' },
                  { label: 'Total users', value: 'totalUsers' },
                ]}
              />
            }
            style={chartCard}
            styles={cardBody}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {`Daily · last ${trendDays} days`}{growthTab === 'activeUsers' ? ' · users who placed a call' : ''}
            </Typography.Text>
            {growthTrend.length === 0 ? (
              <Empty description="No users in this period." style={{ padding: '40px 0' }} />
            ) : (
              <Area
                data={growthTrend}
                xField="day"
                yField="value"
                height={220}
                theme="classicDark"
                style={{ fill: `l(270) 0:rgba(139,92,246,0) 1:${ACCENT_PURPLE}88`, stroke: '#A855F7', lineWidth: 2 }}
              />
            )}
          </Card>
        </Col>
        {can('analytics.revenue.view') && (
          <Col xs={24} lg={12}>
            <Card
              title="Revenue overview"
              extra={
                <Segmented<RevenueTab>
                  size="small"
                  value={revenueTab}
                  onChange={setRevenueTab}
                  options={[
                    { label: 'Money received', value: 'received' },
                    { label: 'Listener earnings', value: 'earnings' },
                  ]}
                />
              }
              style={chartCard}
              styles={cardBody}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {`Daily · last ${trendDays} days`} · <a onClick={() => navigate(dashboardLinks.revenueChart)}>Open analytics →</a>
                </Typography.Text>
                <span style={{ color: 'white', fontWeight: 800, fontSize: 18 }}>{rupees(revenueTotal)}</span>
              </div>
              {revenueTrend.length === 0 ? (
                <Empty description="No money moved in this period." style={{ padding: '40px 0' }} />
              ) : (
                <Area
                  data={revenueTrend}
                  xField="day"
                  yField={revenueTab}
                  height={220}
                  theme="classicDark"
                  axis={{ y: { labelFormatter: (v: number) => rupees(v) } }}
                  tooltip={{ items: [{ channel: 'y', valueFormatter: (v: number) => rupees(v) }] }}
                  style={{ fill: `l(270) 0:rgba(255,208,106,0) 1:${ACCENT_GOLD}66`, stroke: ACCENT_GOLD, lineWidth: 2 }}
                />
              )}
            </Card>
          </Col>
        )}
      </Row>

      {/* ===== Funnel + call outcomes ===== */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={12}>
          <Card
            title="Registration funnel"
            extra={<a onClick={() => navigate(dashboardLinks.incompleteUsers)} style={{ fontSize: 12 }}>View incomplete users →</a>}
            style={chartCard}
            styles={cardBody}
          >
            {kpis.data ? (
              <RegistrationFunnel
                onboarding={kpis.data.onboarding}
                onOpenStep={(step) => navigate(dashboardLinks.incompleteAtStep(step))}
              />
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<span>Calls by outcome <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>· {periodLabel}</Typography.Text></span>}
            style={chartCard}
            styles={cardBody}
          >
            {m ? (
              <Donut
                center={m.calls.total.toLocaleString('en-IN')}
                caption="Total calls"
                slices={[
                  { label: 'Completed', value: m.calls.completed, color: ACCENT_TEAL, to: days ? dashboardLinks.callsInRange(days, { outcome: 'connected' }) : undefined },
                  { label: 'Missed', value: m.calls.missed, color: ACCENT_AMBER, to: days ? dashboardLinks.callsInRange(days, { outcome: 'no_answer' }) : undefined },
                  { label: 'Declined', value: m.calls.declined, color: ACCENT_PINK, to: days ? dashboardLinks.callsInRange(days, { outcome: 'declined' }) : undefined },
                  { label: 'Cancelled', value: m.calls.cancelled, color: ACCENT_PURPLE, to: days ? dashboardLinks.callsInRange(days, { outcome: 'cancelled' }) : undefined },
                  { label: 'Failed', value: m.calls.failed, color: '#9CA3AF', to: days ? dashboardLinks.callsInRange(days, { status: 'failed' }) : undefined },
                ]}
              />
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Minutes, listener activity, listeners ===== */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} md={12} lg={8}>
          <Card title="Audio vs video minutes" style={chartCard} styles={cardBody}>
            {m ? (
              <>
                <BarList
                  format={(n) => minutes(n)}
                  rows={[
                    { label: 'Audio', value: m.calls.audioSeconds, color: ACCENT_BLUE, icon: <AudioOutlined /> },
                    { label: 'Video', value: m.calls.videoSeconds, color: ACCENT_PINK, icon: <VideoCameraOutlined /> },
                    { label: 'Total', value: m.calls.totalSeconds, color: ACCENT_TEAL },
                  ]}
                />
                <div style={{ marginTop: 18 }}>
                  <span style={{ color: 'white', fontSize: 24, fontWeight: 800 }}>
                    {avgCallSeconds === null ? '—' : mmss(avgCallSeconds)}
                  </span>{' '}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>avg. completed call (min:sec)</Typography.Text>
                </div>
              </>
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card
            title={<span>Listener activity <Tag style={{ fontSize: 10, marginLeft: 6 }}>right now</Tag></span>}
            style={chartCard}
            styles={cardBody}
          >
            {now ? (
              <BarList
                rows={[
                  { label: 'Online', value: now.onlineListeners, color: ACCENT_TEAL, to: dashboardLinks.onlineListeners },
                  { label: 'Busy', value: now.busyListeners, color: ACCENT_AMBER, to: dashboardLinks.busyListeners },
                  { label: 'Live', value: now.liveListeners, color: ACCENT_RED, to: dashboardLinks.liveListeners },
                  { label: 'Offline', value: now.offlineListeners, color: MUTED, to: dashboardLinks.allListeners },
                ]}
              />
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
        <Col xs={24} md={24} lg={8}>
          <Card
            title={<span>Listeners <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>· {periodLabel}</Typography.Text></span>}
            style={chartCard}
            styles={cardBody}
          >
            {m ? (
              <BarList
                labelWidth={130}
                rows={[
                  { label: 'Approved (all time)', value: m.listeners.approved, color: ACCENT_TEAL, to: dashboardLinks.allListeners },
                  { label: 'New applications', value: m.listeners.newApplications, color: ACCENT_BLUE, to: dashboardLinks.pendingListeners },
                  { label: 'Rejected', value: m.listeners.rejected, color: ACCENT_RED, to: dashboardLinks.rejectedListeners },
                ]}
              />
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== More figures for the period: every one opens its list ===== */}
      <SectionTitle hint={periodLabel}>More figures</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Total calls" value={m?.calls.total.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.callsInRange(days) : undefined} accent={ACCENT_TEAL} loading={metrics.isLoading} />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Completed" value={m?.calls.completed.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.callsInRange(days, { outcome: 'connected' }) : undefined} accent={ACCENT_TEAL} loading={metrics.isLoading} hint="connected" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Missed" value={m?.calls.missed.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.callsInRange(days, { outcome: 'no_answer' }) : undefined} accent={ACCENT_AMBER} loading={metrics.isLoading} hint="no answer" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Declined" value={m?.calls.declined.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.callsInRange(days, { outcome: 'declined' }) : undefined} accent={ACCENT_AMBER} loading={metrics.isLoading} hint="by the listener" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Failed" value={m?.calls.failed.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.callsInRange(days, { status: 'failed' }) : undefined} accent={ACCENT_RED} loading={metrics.isLoading} hint="dropped, stopped or not connected" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Live streams" value={now?.liveStreams ?? '—'} to={dashboardLinks.liveStreams} accent={ACCENT_PINK} loading={realtime.isLoading} hint="on air now" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Audio minutes" value={m ? minutes(m.calls.audioSeconds) : '—'} to={days ? dashboardLinks.callsInRange(days, { outcome: 'connected' }) : undefined} accent={ACCENT_BLUE} loading={metrics.isLoading} />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Video minutes" value={m ? minutes(m.calls.videoSeconds) : '—'} to={days ? dashboardLinks.callsInRange(days, { outcome: 'connected' }) : undefined} accent={ACCENT_PINK} loading={metrics.isLoading} />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Users who recharged" value={m?.users.usersWhoRecharged.toLocaleString('en-IN') ?? '—'} to={days ? dashboardLinks.rechargesInRange(days) : undefined} accent={ACCENT_GOLD} loading={metrics.isLoading} hint="bought coins at least once" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard
            label="Finished sign-up"
            value={m ? (m.users.registrationCompletionPct === null ? '—' : `${m.users.registrationCompletionPct}%`) : '—'}
            to={dashboardLinks.incompleteUsers}
            accent={ACCENT_PINK}
            loading={metrics.isLoading}
            hint={m ? `${m.users.registrationCompleted} of ${m.users.newUsers} new users` : undefined}
          />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Money received per call" value={m ? (m.money.avgRevenuePerCallPaise === null ? '—' : rupees(m.money.avgRevenuePerCallPaise)) : '—'} to={dashboardLinks.revenueChart} accent={ACCENT_BLUE} loading={metrics.isLoading} hint="estimate: received ÷ completed calls" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Approved listeners" value={m?.listeners.approved.toLocaleString('en-IN') ?? '—'} to={dashboardLinks.allListeners} accent={ACCENT_TEAL} loading={metrics.isLoading} hint="all time" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="New applications" value={m?.listeners.newApplications.toLocaleString('en-IN') ?? '—'} to={dashboardLinks.pendingListeners} accent={ACCENT_BLUE} loading={metrics.isLoading} hint="applied in the period" />
        </Col>
        <Col xs={12} md={8} xl={SEVENTH}>
          <StatCard label="Rejected applications" value={m?.listeners.rejected.toLocaleString('en-IN') ?? '—'} to={dashboardLinks.rejectedListeners} accent={ACCENT_RED} loading={metrics.isLoading} hint="rejected in the period" />
        </Col>
      </Row>

      {/* ===== App problems + live sessions ===== */}
      <Row gutter={[16, 16]} align="stretch">
        {can('incidents.view') && (
          <Col xs={24} lg={14}>
            <Card
              title="Recent app problems"
              extra={<a onClick={() => navigate(dashboardLinks.allIncidents)} style={{ fontSize: 12 }}>View all →</a>}
              style={chartCard}
              styles={cardBody}
            >
              {!recentIncidents.data || recentIncidents.data.items.length === 0 ? (
                <Empty description="No app problems reported." style={{ padding: '40px 0' }} />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Issue</th>
                        <th>Type</th>
                        <th>Severity</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentIncidents.data.items.slice(0, 6).map((i) => (
                        <tr key={i.id} className="dash-row" onClick={() => navigate(dashboardLinks.incident(i.id))}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <WarningOutlined style={{ color: i.severity === 'fatal' || i.severity === 'error' ? ACCENT_RED : ACCENT_GOLD, marginRight: 6 }} />
                            {clock(i.createdAt)}
                          </td>
                          <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.message}>
                            {i.message}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {i.category} · {i.platform}
                          </td>
                          <td>
                            <Tag color={SEVERITY_COLOR[i.severity]} style={{ fontSize: 10 }}>{i.severity}</Tag>
                          </td>
                          <td>{i.status ? <Tag color={STATUS_COLOR[i.status]} style={{ fontSize: 10 }}>{plain(i.status)}</Tag> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </Col>
        )}
        <Col xs={24} lg={can('incidents.view') ? 10 : 24}>
          <Card
            title="Live calls & sessions"
            extra={
              <a
                onClick={() =>
                  navigate(sessionsTab === 'streams' ? dashboardLinks.liveStreams : sessionsTab === 'recent' ? dashboardLinks.calls : dashboardLinks.activeCalls)
                }
                style={{ fontSize: 12 }}
              >
                View all →
              </a>
            }
            style={chartCard}
            styles={cardBody}
          >
            <Segmented<SessionsTab>
              size="small"
              value={sessionsTab}
              onChange={setSessionsTab}
              style={{ marginBottom: 8 }}
              options={[
                { label: `Live calls${activeCalls.data ? ` (${activeCalls.data.items.length})` : ''}`, value: 'calls' },
                ...(can('live.view') ? [{ label: 'Live streams', value: 'streams' as const }] : []),
                { label: 'Recent calls', value: 'recent' },
              ]}
            />
            {sessionsTab === 'calls' &&
              (!activeCalls.data || activeCalls.data.items.length === 0 ? (
                <Empty description="No calls in progress." style={{ padding: '30px 0' }} />
              ) : (
                activeCalls.data.items.slice(0, 6).map((c) =>
                  callRow(c, <span style={{ color: ACCENT_TEAL, fontFamily: 'monospace', fontWeight: 600 }}>{callDuration(c.startedAt)}</span>),
                )
              ))}
            {sessionsTab === 'streams' &&
              (!liveStreams.data || liveStreams.data.items.length === 0 ? (
                <Empty description="Nobody is live." style={{ padding: '30px 0' }} />
              ) : (
                liveStreams.data.items.slice(0, 6).map((s) => (
                  <div
                    key={s.id}
                    onClick={() => navigate(dashboardLinks.liveStreams)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${CARD_BORDER}`, fontSize: 13, cursor: 'pointer' }}
                  >
                    <Avatar size={26} style={{ background: ACCENT_RED + '40', color: ACCENT_RED, fontSize: 11, fontWeight: 700 }}>
                      {(s.streamer.displayName ?? s.streamer.handle).slice(0, 2).toUpperCase()}
                    </Avatar>
                    <span style={{ color: 'white' }}>{s.streamer.displayName ?? `@${s.streamer.handle}`}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {s.title}
                    </Typography.Text>
                    <span style={{ color: ACCENT_RED, fontFamily: 'monospace', fontWeight: 600 }}>{callDuration(s.startedAt)}</span>
                    <Tag style={{ fontSize: 10 }}>{s.viewerCount} watching</Tag>
                  </div>
                ))
              ))}
            {sessionsTab === 'recent' &&
              (!recentCalls.data || recentCalls.data.items.length === 0 ? (
                <Empty description="No calls yet." style={{ padding: '30px 0' }} />
              ) : (
                recentCalls.data.items.slice(0, 6).map((c) =>
                  callRow(
                    c,
                    <Tag style={{ fontSize: 10 }}>{c.durationSeconds ? mmss(c.durationSeconds) : plain(c.outcome ?? c.status ?? '—')}</Tag>,
                  ),
                )
              ))}
          </Card>
        </Col>
      </Row>

      {/* ===== Who the users are ===== */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={12}>
          <Card title="Top languages" extra={<a onClick={() => navigate(dashboardLinks.users)} style={{ fontSize: 12 }}>View users →</a>} style={chartCard} styles={cardBody}>
            {languages ? (
              <BarList
                labelWidth={110}
                rows={[
                  ...languages.top.map((l, i) => ({
                    label: languageName(l.code),
                    value: l.users,
                    color: LANGUAGE_COLORS[i % LANGUAGE_COLORS.length] ?? MUTED,
                    extra: `${pct(l.users, languages.total)}%`,
                  })),
                  ...(languages.other > 0
                    ? [{ label: 'Others', value: languages.other, color: MUTED, extra: `${pct(languages.other, languages.total)}%` }]
                    : []),
                ]}
              />
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Device distribution" style={chartCard} styles={cardBody}>
            {insights.data ? (
              <Donut
                center={deviceTotal.toLocaleString('en-IN')}
                caption="Users"
                slices={insights.data.devices.map((d) => ({
                  label: plain(d.platform),
                  value: d.users,
                  color: PLATFORM_COLOR[d.platform] ?? MUTED,
                }))}
              />
            ) : (
              <Skeleton active />
            )}
          </Card>
        </Col>
      </Row>

      {/* Footer: system state + when the live figures were last read. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 12, color: '#9CA3AF', paddingBottom: 8 }}>
        <span>
          <strong style={{ color: 'white' }}>Dude</strong> · Admin panel
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 6, background: systemOk ? '#34D399' : health.data ? '#F87171' : '#9CA3AF' }} />
          {systemOk ? 'System online' : health.data ? 'System degraded' : 'Checking system…'}
          {now && ` · Last update ${new Date(now.generatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
        </span>
      </div>
    </Space>
  );
}
